import { useEffect, useMemo, useState } from "react";
import { useTimer } from "react-timer-hook";
import { useIdle } from "react-use";
import clsx from "clsx";

import { Clock } from "@hagerty/icons";
import { unstable__Button as Button, Modal } from "@hagerty/react-components";

import { SessionTimeoutWarningProps } from "./SessionTimeoutWarning.types";
import styles from "./SessionTimeoutWarning.module.scss";

type TimerResult = {
  seconds: number;
  minutes: number;
};

/**
 * Displays a modal with a session expiration message after a specified amount of user idle time.
 */
export const SessionTimeoutWarning = ({
  idleTime,
  timeoutDuration,
  onTimeoutExpired,
  onExtendSession,
  onModalDisplayed,
  className,
}: SessionTimeoutWarningProps) => {
  const [showWarning, setShowWarning] = useState(false);
  const timeoutDurationLabel = useMemo(
    () =>
      formatTimeLong(
        Math.floor(timeoutDuration / 1000 / 60),
        Math.floor(timeoutDuration / 1000) % 60
      ),
    [timeoutDuration]
  );

  // This countdown timer is displayed on the dialog so the user knows how much time they have left in their session.
  const timer = useTimer({
    expiryTimestamp: new Date(),
    onExpire: onTimeoutExpired,
    autoStart: false,
  });

  const roundedTime = useMemo(() => getTimeToNearestTenSeconds(timer), [timer]);

  const extendSession = () => {
    setShowWarning(false);
    onExtendSession?.();

    onModalDisplayed?.(false);
    timer.pause();
  };

  const isUserIdle = useIdle(idleTime);

  useEffect(() => {
    if (isUserIdle && !showWarning) {
      setShowWarning(true);
      timer.restart(getExpirationDate(timeoutDuration));

      onModalDisplayed?.(true);
    }
  }, [timer, isUserIdle, showWarning, timeoutDuration, onModalDisplayed]);

  return showWarning ? (
    <Modal
      id="session-timeout-warning-modal"
      size="Small"
      initOpen
      aria-labelledby="session-timeout-warning"
      className={clsx(className)}
      header="Your session is about to expire"
      onClose={extendSession}
      footer={
        <Button
          size="small"
          variant="solid"
          onClick={extendSession}
          className={styles.actionButton}
        >
          Extend session
        </Button>
      }
    >
      <div className={styles.content}>
        <p>
          For your security, this session will be timed out in{" "}
          {timeoutDurationLabel} due to inactivity. To continue, select "Extend
          session" to stay signed in.
        </p>
        <p>
          <time
            className={styles.timeLeftRow}
            role="timer"
            aria-hidden="true"
            dateTime={`PT${timer.minutes}M${timer.seconds}S`}
          >
            <Clock className={styles.clockIcon} />
            <span className={styles.timeLeft}>{formatTime(timer)} left</span>
          </time>
          {/* For screen readers, round countdown to nearest 10 seconds, so it's not obnoxiously telling them every second. */}
          <time
            className="sr-only"
            role="timer"
            aria-live="assertive"
            aria-label={`Your session will timeout in ${formatTimeLong(
              roundedTime.minutes ?? 0,
              roundedTime.seconds ?? 0
            )}`}
            dateTime={`PT${roundedTime.minutes}M${roundedTime.seconds}S`}
          />
        </p>
      </div>
    </Modal>
  ) : null;
};

function getExpirationDate(timeoutDuration: number): Date {
  const date = new Date();
  date.setMilliseconds(date.getMilliseconds() + timeoutDuration);

  return date;
}

function formatTime(timer: TimerResult): string {
  return `${timer.minutes}:${timer.seconds < 10 ? "0" : ""}${timer.seconds}`;
}

function formatTimeLong(minutes: number, seconds: number): string {
  const parts = [];

  if (minutes > 0) parts.push(`${minutes} minutes`);

  if (seconds > 0) parts.push(`${seconds} seconds`);

  return parts.join(" ");
}

function getTimeToNearestTenSeconds(timer: TimerResult): Partial<TimerResult> {
  const seconds = Math.ceil(timer.seconds / 10) * 10;

  return {
    seconds: seconds % 60,
    minutes: timer.minutes + (seconds === 60 ? 1 : 0),
  };
}
